import type {SharedProject} from '../../../services/workflow.service';
import {normalizeFileTypeToken} from '../../../models/ruleset-file-match';

/** Typ eines myChecks */
export type RulesetType = 'KI' | 'HYBRID' | 'IDS';

/** Prüfebene eines Hybrid-Regelsets (bestimmt u. a. 3- vs. 4-Spalten-Bericht). */
export type Pruefebene = 'lv_position' | 'kapitel' | 'element' | 'gesamtdokument';

export const PRUEFEBENE_OPTIONS: { value: Pruefebene; label: string }[] = [
  {value: 'lv_position', label: 'LV-Position'},
  {value: 'kapitel', label: 'Kapitel'},
  {value: 'element', label: 'Element'},
  {value: 'gesamtdokument', label: 'Gesamtdokument'},
];

/** Default-Prüfebene für neue Regelsets (KI- und Hybrid-Editor): Gesamtdokument braucht keinen Identifikator. */
export const DEFAULT_PRUEFEBENE: Pruefebene = 'gesamtdokument';

/**
 * Vorgeschlagene Eingabeparameter je Prüfebene (der erste Eintrag wird als Identifikator vorbelegt).
 * Wird sowohl im KI- als auch im Hybrid-Editor verwendet, um beim Wechsel der Prüfebene passende
 * Eingabeparameter vorzuschlagen (z. B. LV-Position → Ordnungszahl/Kurztext/…).
 */
export const PRUEFEBENE_DEFAULT_PARAMETERS: Record<Pruefebene, string[]> = {
  lv_position: ['Ordnungszahl', 'Kurztext', 'Langtext', 'Menge', 'EP', 'GP'],
  kapitel: ['Kapitelnummer', 'Kapitelbezeichnung'],
  element: ['Elementbezeichnung', 'Elementtyp'],
  gesamtdokument: [],
};

/** Fällt bei ungültigem/fehlendem Wert auf {@link DEFAULT_PRUEFEBENE} zurück. */
export function normalizePruefebene(value: unknown): Pruefebene {
  const candidate = String(value ?? '').trim() as Pruefebene;
  return PRUEFEBENE_OPTIONS.some(option => option.value === candidate) ? candidate : DEFAULT_PRUEFEBENE;
}

/**
 * Datentypen eines Eingabeparameters (identisch zu den `typ`-Werten des eingangsparameter-Knotens).
 *
 * 'Auswahl' schränkt die Extraktion auf die konfigurierten Optionen ein: das Antwortschema des
 * Extraktions-Modells wird dafür zu einem Enum (`check_service._build_position_item_model`), das
 * Modell kann also nichts anderes liefern.
 */
export const EINGABE_PARAMETER_TYPES = ['Text', 'Zahl', 'Währung', 'Liste', 'Auswahl'] as const;

/** Datentyp-Wert, der eine Optionsliste verlangt. Set-Ebene wie Knoten-Ebene. */
export const AUSWAHL_DATA_TYPE = 'Auswahl';

/**
 * Alles, was eine Optionsliste tragen kann: Tag, Prüfparameter, Eingabeparameter (Feld `options`,
 * englisch wie auf Set-Ebene üblich) oder Ausgabeparameter.
 *
 * Der Optionen-Dialog (`OptionsPopupComponent`) arbeitet ausschließlich auf diesem Ausschnitt.
 */
export interface OptionsHolder {
  name?: string;
  options?: string[];
}

/** Set-Parameter mit Optionen (Prüfparameter und Tags haben dieselbe Form). */
export interface SetParameter extends OptionsHolder {
  id: string;
  name: string;
  dataType: string;
  necessity: string;
  options?: string[];
}

/**
 * Ausgabeparameter eines KI-Regelsets – eine Ergebnisspalte.
 *
 * Bei `dateityp: 'Auswahl'` erzwingen die `options` das Antwortformat der KI
 * (`check_service._py_type_for` baut daraus ein Enum). `wert` bleibt die Anweisung an das Modell;
 * dass dort früher auch die Optionen standen, ist Altformat (siehe `migrateOutputParameterOptions`).
 */
export interface SetOutputParameter extends OptionsHolder {
  modus: string;
  name: string;
  wert: string;
  dateityp: string;
  options?: string[];
}

/** Ob ein Parameter eine Optionsliste braucht (Datentyp 'Auswahl', egal in welchem Feld er steht). */
export function needsOptions(param: { dataType?: string; dateityp?: string } | null | undefined): boolean {
  const type = String(param?.dataType ?? param?.dateityp ?? '').trim();
  return type.toLowerCase() === AUSWAHL_DATA_TYPE.toLowerCase();
}

/**
 * Meldungen für Auswahl-Parameter ohne Optionen. Leeres Ergebnis = alles in Ordnung.
 *
 * Warum blockierend und nicht nur ein Hinweis: ohne Optionen fällt der Datentyp zur Laufzeit still
 * auf freien Text zurück – die Regel prüft dann etwas anderes, als im Editor konfiguriert steht,
 * und niemand sieht es. Beide Editoren rufen dieselbe Funktion, damit die Regel nicht auseinander
 * läuft.
 */
export function collectMissingOptionMessages(groups: Array<{
  label: string;
  params: ReadonlyArray<{ name?: string; dataType?: string; dateityp?: string; options?: string[] }>;
}>): string[] {
  const messages: string[] = [];
  for (const group of groups) {
    for (const param of group.params ?? []) {
      if (!needsOptions(param)) continue;
      if ((param.options ?? []).some(option => String(option ?? '').trim())) continue;
      const name = String(param.name ?? '').trim() || '(ohne Namen)';
      messages.push(`${group.label} „${name}“: Datentyp „Auswahl“ ohne Auswahlmöglichkeiten.`);
    }
  }
  return messages;
}

/**
 * `{Platzhalter}` im Prompt, die keine bekannte Variable sind – für die Testlauf-Prüfung („Prüfregel
 * testen"). Die Speichervalidierung nutzt das bewusst (noch) nicht.
 *
 * Als Platzhalter zählt nur, was wie ein Variablenname aussieht: keine Anführungszeichen, kein
 * Doppelpunkt, kein Zeilenumbruch. So bleiben JSON-Beispiele im Prompt (`{"ergebnis": …}`) außen vor.
 * `known` darf mit oder ohne Klammern kommen (`'{Prüfregelname}'` wie `promptVariables` liefert).
 *
 * @returns jede unbekannte Variable einmal, mit Klammern, in der Reihenfolge des ersten Auftretens
 */
export function findUnknownPromptVariables(text: string | null | undefined, known: ReadonlyArray<string>): string[] {
  if (!text) return [];
  const knownNames = new Set(known.map(name => name.trim().replace(/^\{/, '').replace(/\}$/, '').trim()));
  const unknown: string[] = [];
  const seen = new Set<string>();
  for (const match of text.matchAll(/\{([^{}"':\n\r]{1,80})\}/g)) {
    const name = match[1].trim();
    if (!name || knownNames.has(name) || seen.has(name)) continue;
    seen.add(name);
    unknown.push(`{${name}}`);
  }
  return unknown;
}

/** Dateitypen ohne Text-Index – dort extrahiert der Eingangsparameter nichts (Datei-Modus). */
const GEOMETRIE_DATEITYPEN: ReadonlySet<string> = new Set(['IFC', 'COORDINATION']);

/**
 * Erlaubt das Regelset ausschließlich Geometriedateien (IFC, Koordinationsmodell)?
 *
 * Dann reicht jeder Eingangsparameter-Knoten nur die Datei an die IFC-Module weiter
 * (Datei-Modus) – Eingabeparameter, Prüfebene und Identifikator haben keine Wirkung, die
 * Parameter-Pflicht entfällt. Spiegel von `_ist_datei_modus` (Modul eingangsparameter) und
 * `HybridSetConfig#isGeometryOnly` (Backend): eine leere Auswahl heißt „alle Typen“ und zählt
 * nicht, eine gemischte (IFC und PDF) ebenso nicht.
 */
export function isGeometryOnlyFileTypes(fileTypes: ReadonlyArray<string> | null | undefined): boolean {
  const types = new Set((fileTypes ?? []).map(normalizeFileTypeToken).filter(type => type.length > 0));
  return types.size > 0 && [...types].every(type => GEOMETRIE_DATEITYPEN.has(type));
}

/** Mindestens ein benannter Eingabeparameter nötig, damit der Eingangsparameter-Knoten extrahieren kann. */
export function hasConfiguredEingabeparameter(
  params: ReadonlyArray<{ name?: string }> | null | undefined,
): boolean {
  return (params ?? []).some(param => String(param?.name ?? '').trim().length > 0);
}

/**
 * Altformat der Auswahl-Ausgabeparameter: die Optionen standen kommasepariert im `wert`-Feld, weil
 * es kein Optionsfeld gab (`check_service._py_type_for` liest das bis heute als Rückfallebene).
 *
 * Beim Laden im Editor werden sie daraus übernommen, damit ein Bestandsset die neue
 * Pflichtprüfung nicht als Sackgasse erlebt. `wert` bleibt unangetastet – dort steht die Anweisung
 * an das Modell, und ein zweiter Lauf würde sonst nichts mehr finden.
 */
export function migrateOutputParameterOptions(params: SetOutputParameter[]): SetOutputParameter[] {
  return (params ?? []).map(param => {
    if (!needsOptions(param) || (param.options ?? []).length) return param;
    const parsed = String(param.wert ?? '')
      .split(/[,;\n|]+/)
      .map(part => part.trim())
      .filter(Boolean);
    // Ein einzelner Eintrag ist keine Liste, sondern eine Anweisung ohne Trennzeichen – daraus eine
    // Ein-Wert-Auswahl zu machen wäre geraten.
    return parsed.length > 1 ? {...param, options: parsed} : param;
  });
}

/**
 * Auf Regelset-Ebene definierter Eingabeparameter. Wird beim Speichern in den
 * `eingangsparameter`-Knoten jeder Regel synchronisiert (Set = Single Source of Truth).
 * Die `id` ist der stabile Sync-Schlüssel zwischen Set und Regel-Knoten.
 */
export interface SetEingabeparameter extends OptionsHolder {
  id: string;
  name: string;
  beschreibung: string;
  /** 'Text' | 'Zahl' | 'Währung' | 'Liste' | 'Auswahl' */
  dataType: string;
  /** Zulässige Werte bei `dataType: 'Auswahl'` – erzwungen bei der Extraktion. */
  options?: string[];
  /**
   * true = von {@link PRUEFEBENE_DEFAULT_PARAMETERS} beim Wechsel der Prüfebene vorgeschlagen,
   * wird beim nächsten Prüfebene-Wechsel durch die neuen Vorschläge ersetzt. Fehlt/false = vom
   * Nutzer angelegter oder umbenannter Parameter, bleibt bei einem Prüfebene-Wechsel erhalten.
   */
  isPreset?: boolean;
}

/** Prüfmodus */
export type CheckMode = 'SINGLE' | 'COMBINED';

/** Ein myCheck */
export interface Ruleset {
  id: string;
  name: string;
  description: string;
  type: RulesetType;
  status?: 'DRAFT' | 'PUBLISHED' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  ruleCount: number;
  allowedFileTypes: string[];
  allowedDocumentClasses?: string[];
  checkMode: CheckMode;
  isPrebuilt: boolean;
  /**
   * Plattform-global bereitgestelltes Regelwerk. Für normale Nutzer strikt
   * read-only (nur ansehen + ausführen); editierbar nur mit GLOBAL_CHECKRULE_ADMIN im
   * Admin-Bereich. Wird aus dem Backend-Flag bzw. hilfsweise aus isPrebuilt/projectId===null abgeleitet.
   */
  readOnly?: boolean;
  /**
   * Prüfebene des Sets (lv_position | kapitel | element | gesamtdokument). Bestimmt, ob der
   * Bericht 3 (gesamtdokument) oder 4 Spalten (mit Strukturebene) hat. Optional/leer = 3 Spalten.
   */
  pruefebene?: string;
  /** Auf Set-Ebene definierte Eingabeparameter (für die Struktur-Identifikator-Auswahl im Bericht). */
  eingabeparameter?: SetEingabeparameter[];
  /** ID des im Set als Identifikator markierten Eingabeparameters. */
  identifikatorParamId?: string;
  /**
   * KI-Anweisung, welche Einheiten der Prüfebene extrahiert/geprüft werden sollen (z. B. "nur
   * Kapitel zum Brandschutz", "nur LV-Positionen zu Stahlbetonbauteilen", "nur Elemente der
   * Kostengruppe 300"). Nur relevant, wenn pruefebene ≠ gesamtdokument; leer = alle prüfen.
   */
  pruefebeneFilter?: string;
  /** Eigentümerprojekt; bei zentral bereitgestellten Sets `undefined`. */
  projectId?: number;
  /**
   * Nur bei einem zwischen Projekten geteilten Set: alle Projekte, in denen es sichtbar ist,
   * Eigentümer zuerst.
   */
  sharedProjects?: SharedProject[];
  /**
   * Aus einem anderen Projekt der Organisation in dieses geteilt: ausführbar und in Prüfläufen
   * auswählbar, aber nur lesbar – bearbeitet wird es im Eigentümerprojekt. Dann ist auch
   * {@link readOnly} gesetzt, {@link isPrebuilt} aber nicht.
   */
  linkedIn?: boolean;
  /** Für die Sortierung "Zuletzt geändert" – best-effort, nicht bei jeder Quelle verfügbar. */
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

/** Vorschlag für den KI-Assistenten */
export interface RulesetSuggestion {
  title: string;
  description: string;
}

/** Badge-Konfiguration pro Typ */
export const RULESET_TYPE_CONFIG: Record<RulesetType, { label: string; bgClass: string; textClass: string }> = {
  KI: {label: 'KI Prüfung', bgClass: 'bg-blue-100', textClass: 'text-gray-700'},
  HYBRID: {label: 'Hybride Prüfung', bgClass: 'bg-blue-500', textClass: 'text-white'},
  IDS: {label: 'IDS Prüfung', bgClass: 'bg-blue-800', textClass: 'text-white'},
};

/** Eine einzelne Prüfregel innerhalb eines KI-Regelsets */
export interface KiRule {
  id: string;
  name: string;
  /** Kürzel der Prüfregel (max. 10 Zeichen) */
  ruleShortCode?: string;
  description: string;
  category: string;
  allowedDocumentClasses?: string[];
  allowedFileTypes?: string[];
  /** Tag-Werte pro Tag-ID, z.B. { 'uuid-leistungsbild': 'Grundleistungen' } */
  tagValues?: Record<string, string>;
  /** Prüfparameter-Werte pro Parameter-ID, z.B. { 'uuid-identifikation': 'Honorarrechnung' } */
  parameterValues?: Record<string, string>;
}

/** Menüpunkte im KI-Editor */
export type KiEditorSection = 'general' | 'parameters' | 'systemprompt' | 'output-format' | string;

/** Eine einzelne Regel innerhalb eines Hybrid-Regelsets */
export interface HybridRule {
  id: string;
  name: string;
  description: string;
  moduleCount: number;
  allowedDocumentClasses?: string[];
  allowedFileTypes?: string[];
  /** Kürzel der Prüfregel, abgelegt in step.inputs['__shortCode']. */
  shortCode?: string;
  /** Pro Regel ausgefüllte Regelparameter (Tag-Werte), keyed by Tag-ID, z.B. { 'uuid-leistungsbild': 'Grundleistungen' }. */
  tagValues?: Record<string, string>;
}
